import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useFetcher } from "react-router-dom";
import { FieldGrid, Section, StatusBadge } from "@/components/layout-2/ds";
import { InlineAlert } from "@/components/layout-2/ds-settings";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { categoryVisual } from "@/components/dialer/graph/category-visuals";
import { feedingUrl } from "@/components/dialer/feeding/api";
import type { FeedingAlert } from "@/components/dialer/feeding/types";
import { WhyPanel } from "@/components/dialer/feeding/why-panel";
import { CardTimeline } from "@/components/dialer/timeline/card-timeline";
import { EngineState } from "@/components/dialer/engine-state";
import { DialerCardDetail } from "@/types/dialer/card";
import { findNode, GraphView, nodeDisplayLabel } from "@/types/dialer/graph-view";
import { TimelineEntry, TimelineResponse } from "@/types/dialer/timeline";
import { formatParis, utcTooltip } from "@/utils/dialer-datetime";
import { cn } from "@/lib/utils";
import { CircleHelp } from "lucide-react";

/** How often auto-refresh asks for events newer than the current window. */
const AUTO_REFRESH_INTERVAL_MS = 4_000;
/** Keep the soft-enter class until the height + fade animation finishes. */
const ENTERING_CLEAR_MS = 550;

/**
 * What `CardJournalTab` serializes. Declared here rather than in `types/dialer`
 * because it is the shape of a tab payload and not of a dialer endpoint.
 */
export type CardJournalContent = {
    card: DialerCardDetail;
    timeline: TimelineResponse;
    graph: GraphView;
    /** Present when the card comes from the automatic feeding or left the campaign as excluded. */
    why?: { label: string } | null;
    campaign?: { code: string; name: string };
    alert?: FeedingAlert | null;
};

export type CardJournalPanelProps = {
    content: CardJournalContent;
};

/**
 * The record the card was enrolled for. Precedence mirrors `Card.carrier` server-side
 * and the paths mirror the carrier fields of `CardConfig`, so the journal and the
 * Informations tab land on the same record whichever object carries the card.
 */
function cardCarrier(card: DialerCardDetail) {
    if (card.sf_opportunity) return { label: "Opportunity", id: card.sf_opportunity, path: `/opportunities/${card.sf_opportunity}` };
    if (card.sf_case) return { label: "Case", id: card.sf_case, path: `/cases/${card.sf_case}` };
    if (card.sf_account) return { label: "Account", id: card.sf_account, path: `/accounts/${card.sf_account}` };
    if (card.sf_contact) return { label: "Contact", id: card.sf_contact, path: `/contacts/${card.sf_contact}` };
    if (card.sf_lead) return { label: "Lead", id: card.sf_lead, path: `/leads/${card.sf_lead}` };
    return null;
}

/**
 * The diagnosis operators open a card for its business timeline and the interpreter's
 * bookkeeping. The graph remains in the payload to identify the current step and reuse
 * its category colours, but the journal gives the whole width to the chronology.
 *
 * Timeline pagination still posts through the resource route under
 * `/outils/campagnes/fiches/:id/chronologie` — that URL is not a page anymore, only a
 * loader the two "load more" buttons hit.
 */
export function CardJournalPanel({ content }: CardJournalPanelProps) {
    const { card, timeline, graph } = content;
    const engineState = card.engine_state;
    // Two fetchers rather than one, because the direction of a page cannot be read back
    // from its response: a *forward* page reports `has_previous` about its own window,
    // and applying that to the accumulated window would offer to load a history the
    // screen already holds. One fetcher per direction keeps each flag attributable.
    const newerFetcher = useFetcher<TimelineResponse>();
    const olderFetcher = useFetcher<TimelineResponse>();
    const [entries, setEntries] = useState<TimelineEntry[]>(timeline.events ?? []);
    const [hasMore, setHasMore] = useState(Boolean(timeline.has_more));
    const [hasPrevious, setHasPrevious] = useState(Boolean(timeline.has_previous));
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [enteringSequences, setEnteringSequences] = useState<ReadonlySet<number>>(() => new Set());
    const lastSequenceRef = useRef(0);
    const newerFetcherRef = useRef(newerFetcher);
    const clearEnteringTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    newerFetcherRef.current = newerFetcher;

    // Sequences are only unique within one card, so a timer left running across a
    // reset would clear the flag of an unrelated row of the card that replaced it.
    const forgetEntering = useCallback(() => {
        clearEnteringTimers.current.forEach(timer => clearTimeout(timer));
        clearEnteringTimers.current.clear();
        setEnteringSequences(new Set());
    }, []);

    useEffect(() => {
        setEntries(timeline.events ?? []);
        setHasMore(Boolean(timeline.has_more));
        setHasPrevious(Boolean(timeline.has_previous));
        forgetEntering();
    }, [timeline, graph, forgetEntering]);

    useEffect(() => {
        const timers = clearEnteringTimers.current;
        return () => {
            timers.forEach(timer => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    const markEntering = useCallback((arrived: number[]) => {
        if (arrived.length === 0) return;
        setEnteringSequences(previous => {
            const next = new Set(previous);
            arrived.forEach(sequence => next.add(sequence));
            return next;
        });
        arrived.forEach(sequence => {
            const existing = clearEnteringTimers.current.get(sequence);
            if (existing) clearTimeout(existing);
            clearEnteringTimers.current.set(
                sequence,
                setTimeout(() => {
                    clearEnteringTimers.current.delete(sequence);
                    setEnteringSequences(previous => {
                        if (!previous.has(sequence)) return previous;
                        const next = new Set(previous);
                        next.delete(sequence);
                        return next;
                    });
                }, ENTERING_CLEAR_MS)
            );
        });
    }, []);

    // Merged by sequence rather than concatenated, so a double click cannot duplicate a
    // line, a page fetched twice is idempotent, and the two directions extend one
    // contiguous window instead of two lists that could overlap.
    const absorb = useCallback(
        (page: TimelineResponse, { animate = false }: { animate?: boolean } = {}) => {
            let arrived: number[] = [];
            setEntries(previous => {
                const bySequence = new Map(previous.map(entry => [entry.sequence, entry]));
                arrived = [];
                (page.events ?? []).forEach(entry => {
                    if (!bySequence.has(entry.sequence)) arrived.push(entry.sequence);
                    bySequence.set(entry.sequence, entry);
                });
                return [...bySequence.values()].sort((left, right) => left.sequence - right.sequence);
            });
            if (animate) markEntering(arrived);
        },
        [markEntering]
    );

    useEffect(() => {
        if (newerFetcher.state !== "idle" || !newerFetcher.data) return;
        absorb(newerFetcher.data, { animate: true });
        setHasMore(Boolean(newerFetcher.data.has_more));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newerFetcher.state, newerFetcher.data]);

    useEffect(() => {
        if (olderFetcher.state !== "idle" || !olderFetcher.data) return;
        absorb(olderFetcher.data);
        setHasPrevious(Boolean(olderFetcher.data.has_previous));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [olderFetcher.state, olderFetcher.data]);

    const lastSequence = entries.length > 0 ? entries[entries.length - 1].sequence : (timeline.last_sequence ?? 0);
    const firstSequence = entries.length > 0 ? entries[0].sequence : (timeline.first_sequence ?? 0);
    lastSequenceRef.current = lastSequence;

    useEffect(() => {
        if (!autoRefresh) return;

        const loadNewer = () => {
            if (document.hidden) return;
            const fetcher = newerFetcherRef.current;
            if (fetcher.state !== "idle") return;
            fetcher.load(`/outils/campagnes/fiches/${card.id}/chronologie?after_sequence=${lastSequenceRef.current}`);
        };

        loadNewer();
        const interval = window.setInterval(loadNewer, AUTO_REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [autoRefresh, card.id]);

    const carrier = cardCarrier(card);
    const [whyOpen, setWhyOpen] = useState(false);
    const campaignCode = content.campaign?.code ?? card.campaign_code;
    const feedingBase = `/dialer-campaigns/${card.campaign}/feeding`;
    const pinnedVersion = card.workflow;
    const currentNode = findNode(graph, graph.highlight?.current_node_id);
    const currentNodeVisual = categoryVisual(currentNode?.category);
    const CurrentNodeIcon = currentNodeVisual.icon;
    const categoryByNodeId = useMemo(() => Object.fromEntries(graph.graph.nodes.map(node => [node.id, node.category])), [graph.graph.nodes]);

    const linkClass = "text-blue-700 hover:text-blue-900 hover:underline";

    return (
        <div>
            {content.alert && (
                <InlineAlert
                    type={content.alert.type}
                    title={content.alert.title}
                    className="mb-4"
                    action={
                        content.alert.action && (
                            <Button variant="outline" size="sm" asChild>
                                <Link to={`${feedingBase}/${content.alert.action.href}`}>{content.alert.action.label}</Link>
                            </Button>
                        )
                    }
                >
                    {content.alert.text && <span data-testid="card-alert">{content.alert.text}</span>}
                </InlineAlert>
            )}
            <Section icon="IdCard" title="Card">
                <FieldGrid
                    items={[
                        {
                            label: carrier?.label ?? "Carrier",
                            value: carrier ? (
                                <NavLink className={linkClass} to={carrier.path}>
                                    #{carrier.id}
                                </NavLink>
                            ) : null
                        },
                        {
                            label: "Campaign",
                            value: (
                                <NavLink className={linkClass} to={`/dialer-campaigns/${card.campaign}/operations`}>
                                    {card.campaign_name || card.campaign_code}
                                </NavLink>
                            )
                        },
                        { label: "State", value: <StatusBadge value={card.state_badge} /> },
                        { label: "Priority", value: <span className="tabular-nums">{card.priority}</span> },
                        {
                            label: "Next action",
                            value: card.wake_at ? (
                                <SimpleTooltip message={utcTooltip(card.wake_at)}>
                                    <span className="tabular-nums">{formatParis(card.wake_at)}</span>
                                </SimpleTooltip>
                            ) : null
                        },
                        {
                            label: "Attempts",
                            value: (
                                <>
                                    <span className="tabular-nums">{card.call_attempt_count}</span> <span className="text-xs text-gray-500">({card.attempt_count} steps crossed)</span>
                                </>
                            )
                        },
                        { label: "Last qualification", value: card.last_disposition_name },
                        { label: "Assignee", value: card.assignee_name }
                    ]}
                />
                {pinnedVersion && !card.pinned_version_is_deployed && (
                    <Alert variant="info">
                        <AlertTitle>This card does not run the campaign's current version</AlertTitle>
                        <AlertDescription className="flex flex-wrap items-center gap-2">
                            <span>It is pinned to version {pinnedVersion.version} and will finish on it. A deployment chose to let the cards already in flight carry on.</span>
                            <NavLink className={linkClass} to={`/dialer-campaigns/${card.campaign}/operations`}>
                                See the version the campaign runs
                            </NavLink>
                        </AlertDescription>
                    </Alert>
                )}
            </Section>

            <Section
                icon="History"
                title="Timeline"
                actions={
                    <>
                        {content.why && (
                            <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => setWhyOpen(true)} data-testid="card-why">
                                <CircleHelp className="mr-1 h-4 w-4" /> {content.why.label}
                            </Button>
                        )}
                        {currentNode && (
                            <span className="hidden items-center gap-1.5 text-xs text-gray-600 sm:flex">
                                Current step
                                <span className={cn("flex items-center gap-1 rounded-sm px-2 py-0.5 font-medium", currentNodeVisual.header, currentNodeVisual.iconColor)}>
                                    <CurrentNodeIcon className="h-3 w-3" aria-hidden />
                                    {nodeDisplayLabel(currentNode)}
                                </span>
                            </span>
                        )}
                        {pinnedVersion && (
                            <Button variant="outline" size="sm" className="h-7 bg-white" asChild>
                                <NavLink to={`/outils/campagnes/workflows/${pinnedVersion.code}/v/${pinnedVersion.version}`}>
                                    Open {pinnedVersion.code} v{pinnedVersion.version}
                                </NavLink>
                            </Button>
                        )}
                    </>
                }
            >
                <CardTimeline
                    entries={entries}
                    categoryByNodeId={categoryByNodeId}
                    hasMore={hasMore}
                    hasPrevious={hasPrevious}
                    loadingNewer={newerFetcher.state !== "idle"}
                    loadingOlder={olderFetcher.state !== "idle"}
                    onLoadNewer={() => newerFetcher.load(`/outils/campagnes/fiches/${card.id}/chronologie?after_sequence=${lastSequence}`)}
                    onLoadOlder={() => olderFetcher.load(`/outils/campagnes/fiches/${card.id}/chronologie?before_sequence=${firstSequence}`)}
                    enteringSequences={enteringSequences}
                    autoRefresh={autoRefresh}
                    onAutoRefreshChange={setAutoRefresh}
                />
            </Section>

            <WhyPanel url={feedingUrl(campaignCode, `why/card/${card.id}/`)} base={feedingBase} subject={`Card #${card.id}`} open={whyOpen} onOpenChange={setWhyOpen} />

            <EngineState variables={engineState?.variables} attempts={engineState?.attempts} suppressions={engineState?.suppressions} awaiting={engineState?.awaiting} />
        </div>
    );
}
